(() => {
  'use strict';
  if (window.CDGShipping) return;
  const root = window.Shopify?.routes?.root || '/';
  let busy = false;
  const endpoint = '/api/2026-07/graphql.json';
  const error = code => Object.assign(new Error(code), { code });
  async function request(url, options = {}, timeout = 25000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, cache: 'no-store' });
      const body = await response.text();
      if (!response.ok) throw error('service');
      return { body, contentType: response.headers.get('Content-Type') || '' };
    } finally { clearTimeout(timer); }
  }
  async function storefront(query, variables) {
    const { body, contentType } = await request(endpoint, {
      method: 'POST', credentials: 'omit',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json, multipart/mixed' },
      body: JSON.stringify({ query, variables })
    }, 45000);
    const chunks = [];
    if (contentType.includes('multipart/mixed')) {
      const boundary = /boundary="?([^";\s]+)/i.exec(contentType)?.[1];
      if (!boundary) throw error('service');
      for (const part of body.split('--' + boundary)) {
        const separator = /\r?\n\r?\n/.exec(part);
        if (!separator) continue;
        const value = part.slice(separator.index + separator[0].length).trim();
        if (value) chunks.push(JSON.parse(value));
      }
      if (!chunks.length || chunks.at(-1).hasNext !== false) throw error('service');
    } else chunks.push(JSON.parse(body));
    let data = {};
    for (const chunk of chunks) {
      if (chunk.errors?.length) throw error('service');
      if (chunk.data) data = { ...data, ...chunk.data };
      for (const update of chunk.incremental || []) {
        if (update.errors?.length) throw error('service');
        let target = data;
        for (const key of update.path || []) target = target[key] ||= {};
        Object.assign(target, update.data);
      }
    }
    return data;
  }
  const createQuery = 'mutation ShippingEstimate($input: CartInput!) { cartCreate(input:$input) { cart { id totalQuantity cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } } } userErrors { field message } warnings { code message } } }';
  const ratesQuery = 'query ShippingRates($id: ID!) { cart(id:$id) { id ... ShippingDelivery @defer(label: "shipping") } } fragment ShippingDelivery on Cart { deliveryGroups(first:20, withCarrierRates:true) { nodes { deliveryOptions { title description handle estimatedCost { amount currencyCode } } } pageInfo { hasNextPage } } }';
  async function estimate(lines, discountCodes, zip, destination) {
    const input = { lines, discountCodes, buyerIdentity: { countryCode: 'BR' },
      delivery: { addresses: [{ selected: true, address: { deliveryAddress: {
        countryCode: 'BR', provinceCode: destination.state, city: destination.city, zip
      } } }] } };
    const response = await storefront(createQuery, { input });
    const created = response.cartCreate;
    const expected = lines.reduce((sum, line) => sum + line.quantity, 0);
    if (created?.userErrors?.length || !created?.cart || created.cart.totalQuantity !== expected ||
      created.warnings?.some(warning => /MERCHANDISE|QUANTITY|STOCK/.test(warning.code))) throw error('unavailable');
    const id = created.cart.id;
    let delivery;
    for (let attempt = 0; attempt < 2; attempt++) {
      const response = await storefront(ratesQuery, { id });
      delivery = response.cart?.deliveryGroups;
      if (delivery?.nodes?.length && delivery.nodes.every(group => group.deliveryOptions.length)) break;
    }
    if (!delivery || delivery.pageInfo?.hasNextPage) throw error('service');
    if (delivery.nodes.length > 1) throw error('splitDelivery');
    const rates = (delivery.nodes[0]?.deliveryOptions || []).map(option => ({
      name: option.title, description: option.description, price: option.estimatedCost.amount,
      currency: option.estimatedCost.currencyCode
    }));
    return { rates, subtotal: Math.round(Number(created.cart.cost.totalAmount.amount) * 100) };
  }
  async function address(zip) {
    let notFound = 0;
    const providers = [
      { url: 'https://viacep.com.br/ws/' + zip + '/json/', state: 'uf', city: 'localidade' },
      { url: 'https://brasilapi.com.br/api/cep/v2/' + zip, state: 'state', city: 'city' }
    ];
    for (const provider of providers) {
      try {
        const response = await fetchWithTimeout(provider.url, 6500);
        const data = response.data;
        if (response.status === 404 || data.erro) { notFound++; continue; }
        if (response.ok && /^[A-Z]{2}$/.test(data[provider.state] || '')) {
          return { state: data[provider.state], city: data[provider.city] || '' };
        }
      } catch (_) {}
    }
    if (notFound === 2) throw error('zipNotFound');
    throw error('addressService');
  }
  async function fetchWithTimeout(url, timeout) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
      return { status: response.status, ok: response.ok, data: await response.json() };
    }
    finally { clearTimeout(timer); }
  }
  function message(element, text, className = '') {
    const node = document.createElement('p');
    node.className = className;
    node.textContent = text;
    element.replaceChildren(node);
  }
  function texts(calculator) {
    return JSON.parse(calculator.querySelector('[data-cdg-shipping-texts]').textContent);
  }
  function selected(calculator) {
    const section = calculator.closest('.shopify-section') || document;
    const productId = calculator.dataset.productId;
    const forms = [...section.querySelectorAll('form[action*="/cart/add"]')];
    const form = forms.find(item => item.getAttribute('id') === 'product_form_' + productId ||
      item.querySelector('[name="product-id"]')?.value === productId);
    const variantId = form?.querySelector('[name="id"]')?.value;
    const quantityInput = section.querySelector('[data-quantity-input][id="Quantity-Input-' + productId + '"]') ||
      form?.querySelector('[name="quantity"]') ||
      section.querySelector('[name="quantity"][form="' + form?.getAttribute('id') + '"]');
    const quantity = quantityInput ? Number(quantityInput.value) : 1;
    if (!variantId || !Number.isSafeInteger(quantity) || quantity < 1) throw error('selection');
    return { variantId, quantity };
  }
  function display(calculator, result, t, subtotal) {
    const output = calculator.querySelector('.shipping-rates');
    if (!result.length) { message(output, t.noRates, 'error'); return; }
    const list = document.createElement('ul');
    if (subtotal >= 19900) {
      const heading = document.createElement('p');
      heading.textContent = t.beforeDiscounts;
      output.replaceChildren(heading);
    } else output.replaceChildren();
    const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
    result.slice().sort((a, b) => Number(a.price) - Number(b.price)).forEach(rate => {
      if (rate.price == null || (typeof rate.price === 'string' && !rate.price.trim())) return;
      const price = Number(rate.price);
      if (!Number.isFinite(price) || price < 0) return;
      const item = document.createElement('li');
      const title = document.createElement('strong');
      title.textContent = rate.presentment_name || rate.name || t.delivery;
      item.append(title, document.createTextNode(' — ' + (price === 0 ? t.free : money.format(price))));
      const days = (rate.delivery_days || []).map(Number).filter(day => Number.isFinite(day) && day >= 0);
      if (days.length) {
        const min = Math.min(...days), max = Math.max(...days);
        const range = min === max ? String(min) : min + '–' + max;
        item.append(document.createTextNode(' · ' + range + ' ' + (max === 1 ? t.day : t.days)));
      }
      list.append(item);
    });
    if (!list.children.length) throw error('service');
    output.append(list);
    const note = document.createElement('p');
    note.className = 'cdg-shipping-note';
    note.textContent = subtotal >= 19900 ? t.freeCheckout : t.estimate;
    output.append(note);
  }
  async function calculate(calculator) {
    const t = texts(calculator);
    const output = calculator.querySelector('.shipping-rates');
    const input = calculator.querySelector('.custom_zip_code');
    const zip = input.value.replace(/\D/g, '');
    if (!/^\d{8}$/.test(zip)) { message(output, t.zipInvalid, 'error'); input.focus(); return; }
    if (busy) return;
    const revision = calculator.dataset.revision || '0';
    let originalSelection;
    try {
      if (calculator.dataset.template === 'product') originalSelection = JSON.stringify(selected(calculator));
    } catch (_) { message(output, t.selection, 'error'); return; }
    busy = true;
    const buttons = [...document.querySelectorAll('.custom_shipping_button')];
    buttons.forEach(button => { button.disabled = true; });
    calculator.setAttribute('aria-busy', 'true');
    message(output, t.loading, 'loading');
    let result, subtotal, failure;
    try {
      const destination = await address(zip);
      const current = JSON.parse((await request(root + 'cart.js')).body);
      const lines = current.items.map(item => ({
        merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
        quantity: item.quantity,
        ...(item.selling_plan_allocation ? { sellingPlanId: 'gid://shopify/SellingPlan/' + item.selling_plan_allocation.selling_plan.id } : {})
      }));
      if (calculator.dataset.template === 'product') {
        const choice = JSON.parse(originalSelection);
        const merchandiseId = 'gid://shopify/ProductVariant/' + choice.variantId;
        const existing = lines.filter(line => line.merchandiseId === merchandiseId && !line.sellingPlanId)
          .reduce((sum, line) => sum + line.quantity, 0);
        if (existing < choice.quantity) lines.push({ merchandiseId, quantity: choice.quantity - existing });
      }
      if (!lines.length) throw error('empty');
      if (lines.length > 250) throw error('splitDelivery');
      const codes = (current.discount_codes || []).map(discount => discount.code).filter(Boolean);
      const estimated = await estimate(lines, codes, zip, destination);
      result = estimated.rates;
      subtotal = estimated.subtotal;
    } catch (cause) { failure = cause; }
    finally {
      busy = false;
      buttons.forEach(button => { button.disabled = false; });
      calculator.removeAttribute('aria-busy');
    }
    if (input.value.replace(/\D/g, '') !== zip || (calculator.dataset.revision || '0') !== revision) {
      failure = error('selectionChanged');
    }
    if (!failure && originalSelection) {
      try {
        if (JSON.stringify(selected(calculator)) !== originalSelection) failure = error('selectionChanged');
      } catch (_) { failure = error('selectionChanged'); }
    }
    if (failure) message(output, t[failure.code] || t.service, 'error');
    else {
      try { display(calculator, result, t, subtotal); }
      catch (_) { message(output, t.service, 'error'); }
    }
  }
  function initialize() {
    document.querySelectorAll('[data-cdg-shipping]').forEach(calculator => {
      if (calculator.dataset.initialized) return;
      calculator.dataset.initialized = 'true';
      const input = calculator.querySelector('.custom_zip_code');
      input.addEventListener('input', () => {
        const digits = input.value.replace(/\D/g, '').slice(0, 8);
        input.value = digits.length > 5 ? digits.slice(0, 5) + '-' + digits.slice(5) : digits;
        calculator.querySelector('.shipping-rates').replaceChildren();
      });
      input.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); calculate(calculator); }
      });
      calculator.querySelector('.custom_shipping_button').addEventListener('click', () => calculate(calculator));
    });
  }
  function invalidateEstimates() {
    document.querySelectorAll('[data-cdg-shipping]').forEach(calculator => {
      calculator.dataset.revision = String(Number(calculator.dataset.revision || 0) + 1);
      calculator.querySelector('.shipping-rates').replaceChildren();
    });
  }
  function synchronizeQuantity(field) {
    const section = field.closest('.shopify-section');
    const calculator = section?.querySelector('[data-cdg-shipping][data-template="product"]');
    if (!calculator || field.getAttribute('id') !== 'Quantity-Input-' + calculator.dataset.productId) return;
    const form = [...section.querySelectorAll('form[action*="/cart/add"]')].find(item =>
      item.getAttribute('id') === 'product_form_' + calculator.dataset.productId ||
      item.querySelector('[name="product-id"]')?.value === calculator.dataset.productId);
    const quantity = form?.querySelector('[name="quantity"]');
    if (quantity) quantity.value = field.value;
  }
  function handleInput(event) {
    if (!event.target.matches('input[data-quantity-input], .custom_zip_code, [name="id"], [name="quantity"]')) return;
    if (event.target.matches('input[data-quantity-input]')) synchronizeQuantity(event.target);
    invalidateEstimates();
  }
  document.addEventListener('input', handleInput, true);
  document.addEventListener('change', handleInput, true);
  document.addEventListener('click', event => {
    if (event.target.closest('[data-add-quantity], [data-subtract-quantity], [data-remove-item], [data-cart-item-remove]')) invalidateEstimates();
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches('form[action*="/cart/add"]')) return;
    const section = event.target.closest('.shopify-section');
    const calculator = section?.querySelector('[data-cdg-shipping][data-template="product"]');
    if (!calculator || event.target.getAttribute('id') !== 'product_form_' + calculator.dataset.productId) return;
    const field = section.querySelector('[data-quantity-input][id="Quantity-Input-' + calculator.dataset.productId + '"]');
    if (!field) return;
    const quantity = Number(field.value);
    if (!Number.isSafeInteger(quantity) || quantity < 1) {
      event.preventDefault();
      event.stopImmediatePropagation();
      field.focus();
      field.reportValidity();
      return;
    }
    synchronizeQuantity(field);
  }, true);
  document.addEventListener('shopify:section:load', initialize);
  new MutationObserver(mutations => {
    if (mutations.some(mutation => [...mutation.addedNodes].some(node => node.nodeType === 1 &&
      (node.matches('[data-cdg-shipping]') || node.querySelector('[data-cdg-shipping]'))))) initialize();
  }).observe(document.documentElement, { childList: true, subtree: true });
  window.CDGShipping = { calculate, initialize };
  window.CustomShippingInit = event => {
    event.preventDefault();
    const calculator = event.target.closest('[data-cdg-shipping]');
    if (calculator) calculate(calculator);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();

